// Material 3 Button Component
// Aligned with Google Material Web (material-components/material-web/docs/components/button.md)
// Supports: Filled, Filled Tonal, Elevated, Outlined, and Text button types with forwardRef
(function () {
  const { Button: MuiButton } = MaterialUI;

  const Button = React.forwardRef(function Button({
    variant = 'filled', // 'filled' | 'tonal' | 'outlined' | 'elevated' | 'text'
    color = 'primary',  // 'primary' | 'secondary' | 'error' | 'inherit'
    size = 'medium',    // 'small' | 'medium' | 'large'
    startIcon,
    endIcon,
    icon,               // Material Web slot="icon" leading icon
    trailingIcon,       // Material Web trailingIcon
    children,
    disabled = false,
    fullWidth = false,
    href,
    target,
    sx = {},
    ...props
  }, ref) {
    const { m3Colors, m3Shape, m3Elevation } = window.Components || {};
    const theme = MaterialUI.useTheme();
    const isDark = theme?.palette?.mode !== 'light';

    const leading = startIcon || icon;
    const trailing = endIcon || trailingIcon;

    // Base M3 styling
    let muiVariant = 'contained';
    let customStyles = {
      borderRadius: m3Shape ? m3Shape.full : 9999,
      textTransform: 'none',
      fontWeight: 600,
      letterSpacing: '0.1px',
      transition: 'all 0.2s cubic-bezier(0.2, 0, 0, 1)',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 1
    };

    if (size === 'small') {
      customStyles.height = 34;
      customStyles.padding = '0 16px';
      customStyles.fontSize = '0.8125rem';
    } else if (size === 'large') {
      customStyles.height = 48;
      customStyles.padding = '0 28px';
      customStyles.fontSize = '0.9375rem';
    } else {
      customStyles.height = 40;
      customStyles.padding = '0 22px';
      customStyles.fontSize = '0.875rem';
    }

    const colors = theme?.palette?.m3 || (isDark ? window.Components?.m3DarkColors : window.Components?.m3LightColors) || {};

    switch (variant) {
      case 'filled':
        muiVariant = 'contained';
        if (color === 'primary') {
          customStyles.backgroundColor = colors.primary || 'var(--m3-primary)';
          customStyles.color = colors.onPrimary || (isDark ? '#381e72' : '#ffffff');
          customStyles['&:hover'] = {
            backgroundColor: colors.primary || (isDark ? '#e8ddff' : '#5f489b'),
            filter: 'brightness(1.08)',
            boxShadow: isDark ? (m3Elevation ? m3Elevation.level1 : '0 1px 3px rgba(0,0,0,0.3)') : '0 1px 3px rgba(0,0,0,0.15)'
          };
        } else if (color === 'error') {
          customStyles.backgroundColor = colors.error || (isDark ? '#f2b8b5' : '#ba1a1a');
          customStyles.color = colors.onError || (isDark ? '#601410' : '#ffffff');
          customStyles['&:hover'] = {
            backgroundColor: colors.error || (isDark ? '#f9dedc' : '#93000a'),
            filter: 'brightness(1.08)'
          };
        }
        break;

      case 'tonal':
      case 'filled-tonal':
        muiVariant = 'contained';
        customStyles.backgroundColor = colors.secondaryContainer || (isDark ? '#4a4458' : '#e8def8');
        customStyles.color = colors.onSecondaryContainer || (isDark ? '#e8def8' : '#1d192b');
        customStyles.boxShadow = 'none';
        customStyles['&:hover'] = {
          backgroundColor: colors.secondaryContainer || (isDark ? '#585267' : '#ded4ee'),
          filter: 'brightness(1.08)',
          boxShadow: isDark ? (m3Elevation ? m3Elevation.level1 : '0 1px 3px rgba(0,0,0,0.3)') : '0 1px 3px rgba(0,0,0,0.1)'
        };
        break;

      case 'elevated':
        muiVariant = 'contained';
        customStyles.backgroundColor = colors.surfaceContainer || ('var(--m3-surface-container)');
        customStyles.color = colors.primary || 'var(--m3-primary)';
        customStyles.boxShadow = isDark ? (m3Elevation ? m3Elevation.level1 : '0 1px 3px rgba(0,0,0,0.3)') : '0 1px 3px rgba(0,0,0,0.1)';
        customStyles['&:hover'] = {
          backgroundColor: colors.surfaceContainerHigh || (isDark ? '#2b2930' : '#e6e0e9'),
          boxShadow: isDark ? (m3Elevation ? m3Elevation.level2 : '0 2px 6px rgba(0,0,0,0.4)') : '0 2px 6px rgba(0,0,0,0.15)'
        };
        break;

      case 'outlined':
        muiVariant = 'outlined';
        customStyles.backgroundColor = 'transparent';
        customStyles.borderColor = color === 'error'
          ? (colors.error || (isDark ? 'rgba(242, 184, 181, 0.5)' : 'rgba(186, 26, 26, 0.5)'))
          : (colors.outlineVariant || ('var(--m3-outline-variant)'));
        customStyles.color = color === 'error'
          ? (colors.error || (isDark ? '#f2b8b5' : '#ba1a1a'))
          : (colors.primary || 'var(--m3-primary)');
        customStyles['&:hover'] = {
          borderColor: color === 'error' ? (colors.error || '#ba1a1a') : (colors.primary || '#6750a4'),
          backgroundColor: color === 'error'
            ? 'color-mix(in srgb, var(--m3-error) 8%, transparent)'
            : 'color-mix(in srgb, var(--m3-on-surface) 8%, transparent)'
        };
        break;

      case 'text':
      default:
        if (variant === 'text') {
          muiVariant = 'text';
          customStyles.backgroundColor = 'transparent';
          customStyles.color = color === 'error'
            ? (colors.error || (isDark ? 'var(--m3-error)' : '#ba1a1a'))
            : (colors.primary || 'var(--m3-primary)');
          customStyles.padding = size === 'small' ? '0 10px' : '0 14px';
          customStyles['&:hover'] = {
            backgroundColor: color === 'error' ? 'color-mix(in srgb, var(--m3-error) 8%, transparent)' : 'color-mix(in srgb, var(--m3-on-surface) 8%, transparent)'
          };
        }
        break;
    }

    return (
      <MuiButton
        ref={ref}
        variant={muiVariant}
        disabled={disabled}
        fullWidth={fullWidth}
        href={href}
        target={target}
        startIcon={leading}
        endIcon={trailing}
        sx={{
          ...customStyles,
          ...sx
        }}
        {...props}
      >
        {children}
      </MuiButton>
    );
  });

  // Convenient Material Web explicit aliases
  const FilledButton = React.forwardRef((props, ref) => <Button ref={ref} variant="filled" {...props} />);
  const FilledTonalButton = React.forwardRef((props, ref) => <Button ref={ref} variant="tonal" {...props} />);
  const ElevatedButton = React.forwardRef((props, ref) => <Button ref={ref} variant="elevated" {...props} />);
  const OutlinedButton = React.forwardRef((props, ref) => <Button ref={ref} variant="outlined" {...props} />);
  const TextButton = React.forwardRef((props, ref) => <Button ref={ref} variant="text" {...props} />);

  window.Components = window.Components || {};
  window.Components.Button = Button;
  window.Components.FilledButton = FilledButton;
  window.Components.FilledTonalButton = FilledTonalButton;
  window.Components.ElevatedButton = ElevatedButton;
  window.Components.OutlinedButton = OutlinedButton;
  window.Components.TextButton = TextButton;
})();
